import React, { useState, useEffect } from 'react';
import { FiX, FiDollarSign, FiTrendingDown, FiAlertCircle, FiRefreshCw, FiCheckCircle, FiArrowRight } from 'react-icons/fi';
import { getFinopsRecommendations, getAiCache, saveAiCache, type FinopsReport, type FinopsRecommendation, type AiCacheHistoryEntry } from '../../api/ai';
import AiCacheActions from './AiCacheActions';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  nodes: any[];
  edges: any[];
  provider: string;
  environment: string;
  architectureName?: string;
  costs?: any;
  architectureId?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  rightsizing: 'Rightsizing',
  reserved: 'Reserved Instances',
  spot: 'Spot / Preemptible',
  architecture: 'Architecture',
  unused: 'Ressources inutilisées',
  storage: 'Stockage',
};

const EFFORT_COLOR: Record<string, string> = {
  low: 'text-green-600',
  medium: 'text-yellow-600',
  high: 'text-red-600',
};

const ASSESSMENT_CONFIG: Record<string, { color: string; label: string }> = {
  'under-optimized': { color: 'text-red-600', label: 'Non optimisé' },
  'average':         { color: 'text-yellow-600', label: 'Dans la moyenne' },
  'well-optimized':  { color: 'text-green-600', label: 'Bien optimisé' },
};

function SavingsBar({ current, optimized }: { current: number | null; optimized: number }) {
  if (!current) return null;
  const pct = Math.round(((current - optimized) / current) * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-gray-500">
        <span>Actuel</span><span>Optimisé</span>
      </div>
      <div className="relative h-6 bg-gray-100 rounded-full overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-red-300 rounded-full" style={{ width: '100%' }} />
        <div className="absolute inset-y-0 left-0 bg-green-400 rounded-full transition-all duration-700" style={{ width: `${100 - pct}%` }} />
      </div>
      <div className="flex justify-between text-sm font-semibold">
        <span className="text-red-600">{current.toFixed(0)} USD/mois</span>
        <span className="text-green-600">{optimized.toFixed(0)} USD/mois</span>
      </div>
    </div>
  );
}

function RecommendationCard({ r }: { r: FinopsRecommendation }) {
  return (
    <div className="border border-gray-100 rounded-xl p-3 hover:bg-gray-50 transition-colors">
      <div className="flex items-start gap-3">
        <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-bold flex items-center justify-center shrink-0">{r.priority}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-medium text-gray-800">{r.title}</span>
            <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">{CATEGORY_LABELS[r.category] || r.category}</span>
          </div>
          <p className="text-xs text-gray-600 mb-2">{r.description}</p>
          <div className="flex items-center gap-1.5 text-xs text-blue-700 mb-1">
            <FiArrowRight size={11} />
            <span>{r.action}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {r.estimatedSavings > 0 && (
              <span className="font-semibold text-green-600">-{r.estimatedSavings} USD/mois</span>
            )}
            <span>Effort : <span className={EFFORT_COLOR[r.effort]}>{r.effort}</span></span>
            <span>Risque : <span className={EFFORT_COLOR[r.risk]}>{r.risk}</span></span>
          </div>
        </div>
      </div>
    </div>
  );
}

const FinOpsModal: React.FC<Props> = ({ isOpen, onClose, nodes, edges, provider, environment, architectureName, costs, architectureId }) => {
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [report, setReport]     = useState<FinopsReport | null>(null);
  const [metadata, setMetadata] = useState<{ durationMs: number; tokensUsed: number } | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [cacheUpdatedAt, setCacheUpdatedAt] = useState<string | undefined>();
  const [cacheContent, setCacheContent] = useState<any>(null);

  useEffect(() => {
    if (!isOpen) return;
    setReport(null); setError(null); setFromCache(false);
    setCacheUpdatedAt(undefined); setCacheContent(null);
    loadOrGenerate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadOrGenerate = async () => {
    if (architectureId) {
      try {
        const cached = await getAiCache(architectureId, 'finops');
        setReport(cached.content.report);
        setMetadata(cached.content.metadata ? { durationMs: cached.content.metadata.durationMs, tokensUsed: cached.content.metadata.tokensUsed } : null);
        setFromCache(true);
        setCacheUpdatedAt(cached.updatedAt);
        setCacheContent(cached.content);
        return;
      } catch {
        // no cache, generate fresh
      }
    }
    generate();
  };

  const generate = async () => {
    setLoading(true); setError(null); setFromCache(false); setCacheUpdatedAt(undefined);
    try {
      const result = await getFinopsRecommendations({ nodes, edges, provider, environment, costs });
      setReport(result.report);
      setMetadata({ durationMs: result.metadata.durationMs, tokensUsed: result.metadata.tokensUsed });
      const payload = { report: result.report, metadata: result.metadata };
      setCacheContent(payload);
      if (architectureId) {
        saveAiCache(architectureId, 'finops', payload).catch(() => {});
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (entry: AiCacheHistoryEntry) => {
    setReport(entry.content.report);
    setMetadata(entry.content.metadata ? { durationMs: entry.content.metadata.durationMs, tokensUsed: entry.content.metadata.tokensUsed } : null);
    setFromCache(true);
    setCacheUpdatedAt(entry.savedAt);
    setCacheContent(entry.content);
  };

  const handleExportPdf = () => {
    if (!report) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>FinOps — ${architectureName || 'Architecture'}</title>
    <style>body{font-family:system-ui,sans-serif;padding:32px;max-width:900px;margin:0 auto;color:#111}
    h1{font-size:20px;font-weight:700}h2{font-size:16px;font-weight:600;margin:20px 0 8px;border-bottom:1px solid #e5e7eb;padding-bottom:4px}
    .card{border:1px solid #e5e7eb;border-radius:8px;padding:12px 16px;margin:6px 0}
    .savings{font-size:24px;font-weight:700;color:#16a34a}ul{margin:6px 0;padding-left:20px}li{margin:3px 0;font-size:13px}
    @media print{body{padding:16px}}</style></head><body>`);
    w.document.write(`<h1>Recommandations FinOps — ${architectureName || ''}</h1>`);
    w.document.write(`<p style="font-size:14px">${report.summary}</p>`);
    if (report.potentialSavings > 0) {
      w.document.write(`<div class="card"><p>Économies potentielles : <span class="savings">${report.potentialSavings.toFixed(0)} USD/mois</span> (-${report.savingsPercent.toFixed(0)}%)</p></div>`);
    }
    if (report.quickWins?.length) {
      w.document.write('<h2>Quick wins</h2><ul>');
      report.quickWins.forEach(q => w.document.write(`<li>${q}</li>`));
      w.document.write('</ul>');
    }
    if (report.recommendations?.length) {
      w.document.write('<h2>Recommandations</h2>');
      report.recommendations.sort((a, b) => a.priority - b.priority).forEach(r => {
        w.document.write(`<div class="card"><strong>${r.priority}. ${r.title}</strong><p style="font-size:13px;margin:4px 0">${r.description}</p><p style="font-size:12px;color:#4b5563">→ ${r.action} | Économies : ${r.estimatedSavings} USD/mois | Effort : ${r.effort}</p></div>`);
      });
    }
    w.document.write('</body></html>');
    w.document.close();
    w.print();
  };

  if (!isOpen) return null;

  const assessment = report?.benchmarks?.assessment
    ? ASSESSMENT_CONFIG[report.benchmarks.assessment]
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">

        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center">
              <FiDollarSign size={18} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Recommandations FinOps</h2>
              {architectureName && <p className="text-xs text-gray-500">{architectureName}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {report && (
              <button onClick={generate} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                <FiRefreshCw size={12} /> Regénérer
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"><FiX size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Analyse FinOps en cours…</p>
            </div>
          )}

          {error && (
            <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <FiAlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-medium text-red-700">Erreur</p>
                <p className="text-xs text-red-600 mt-1">{error}</p>
                <button onClick={generate} className="mt-2 text-xs text-red-700 underline">Réessayer</button>
              </div>
            </div>
          )}

          {report && (
            <div className="p-6 space-y-6">
              <p className="text-sm text-gray-700 leading-relaxed">{report.summary}</p>

              {/* Économies potentielles */}
              {report.potentialSavings > 0 && (
                <div className="p-5 bg-green-50 border border-green-100 rounded-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <FiTrendingDown size={20} className="text-green-600" />
                    <div>
                      <p className="text-sm font-semibold text-green-800">
                        Économies potentielles : {report.potentialSavings.toFixed(0)} USD/mois
                        {report.savingsPercent > 0 && <span className="ml-2 text-green-600">(-{report.savingsPercent.toFixed(0)}%)</span>}
                      </p>
                    </div>
                  </div>
                  <SavingsBar current={report.currentMonthlyCost} optimized={report.optimizedMonthlyCost} />
                </div>
              )}

              {/* Benchmark */}
              {assessment && (
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-xs font-semibold text-gray-600 mb-1">{report.benchmarks.label}</p>
                  <p className={`text-sm font-bold ${assessment.color}`}>{assessment.label}</p>
                  <p className="text-xs text-gray-600 mt-1">{report.benchmarks.comment}</p>
                </div>
              )}

              {/* Quick wins */}
              {report.quickWins?.length > 0 && (
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
                  <h4 className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1.5">
                    <FiCheckCircle size={13} /> Quick wins
                  </h4>
                  <ul className="space-y-1">
                    {report.quickWins.map((w, i) => (
                      <li key={i} className="text-xs text-blue-800 flex items-start gap-1.5">
                        <span className="text-blue-500 mt-0.5">→</span> {w}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommandations */}
              {report.recommendations?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">
                    Recommandations ({report.recommendations.length})
                  </h3>
                  <div className="space-y-2">
                    {report.recommendations.sort((a, b) => a.priority - b.priority).map((r, i) => (
                      <RecommendationCard key={i} r={r} />
                    ))}
                  </div>
                </div>
              )}

              {/* Actions long terme */}
              {report.longTermActions?.length > 0 && (
                <div className="p-4 bg-purple-50 border border-purple-100 rounded-xl">
                  <h4 className="text-xs font-semibold text-purple-700 mb-2">Actions long terme</h4>
                  <ul className="space-y-1">
                    {report.longTermActions.map((a, i) => (
                      <li key={i} className="text-xs text-purple-800 flex items-start gap-1.5">
                        <span className="text-purple-400 mt-0.5">•</span> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {(metadata || report) && (
          <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400 shrink-0">
            <AiCacheActions
              architectureId={architectureId}
              cacheType="finops"
              content={cacheContent}
              fromCache={fromCache}
              updatedAt={cacheUpdatedAt}
              onRestore={handleRestore}
              onExportPdf={handleExportPdf}
            />
            <div className="flex items-center gap-4">
              {metadata && <><span>{metadata.tokensUsed.toLocaleString()} tokens</span><span>{(metadata.durationMs / 1000).toFixed(1)}s</span></>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinOpsModal;
